import { FC, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';

import {
  EnumCutsheetAssignmentRuleSetRulesInspectionLevel,
  EnumCutsheetAssignmentRuleSetRulesSplitType,
  EnumUserType,
} from 'lib/graphql';

import { SearchableCustomer, useCustomerSearch } from './customer-search';
import {
  INSPECTION_LABELS,
  LocalRule,
  SPLIT_INDEX_ELIGIBLE,
  SPLIT_LABELS,
} from './rule-dialog';

interface TestOrder {
  inspectionLevel?: EnumCutsheetAssignmentRuleSetRulesInspectionLevel;
  splitType?: EnumCutsheetAssignmentRuleSetRulesSplitType;
  splitIndex?: number;
  producerId?: string;
  buyerId?: string;
}

const EMPTY_ORDER: TestOrder = {
  inspectionLevel: undefined,
  splitType: undefined,
  splitIndex: undefined,
  producerId: undefined,
  buyerId: undefined,
};

interface DimensionResult {
  name: string;
  passed: boolean;
  detail: string;
}

interface RuleEvaluation {
  ruleIndex: number;
  dimensions: DimensionResult[];
  allPassed: boolean;
}

function evaluateRule(order: TestOrder, rule: LocalRule): RuleEvaluation {
  const dimensions: DimensionResult[] = [];

  if (rule.inspectionLevel.length === 0) {
    dimensions.push({
      name: 'Inspection Level',
      passed: true,
      detail: 'rule matches any',
    });
  } else if (!order.inspectionLevel) {
    dimensions.push({
      name: 'Inspection Level',
      passed: false,
      detail: `rule requires ${rule.inspectionLevel.map((v) => INSPECTION_LABELS[v]).join(' or ')}, order has none`,
    });
  } else if (rule.inspectionLevel.includes(order.inspectionLevel)) {
    dimensions.push({
      name: 'Inspection Level',
      passed: true,
      detail: `order is ${INSPECTION_LABELS[order.inspectionLevel]}`,
    });
  } else {
    dimensions.push({
      name: 'Inspection Level',
      passed: false,
      detail: `order is ${INSPECTION_LABELS[order.inspectionLevel]}, rule requires ${rule.inspectionLevel.map((v) => INSPECTION_LABELS[v]).join(' or ')}`,
    });
  }

  if (rule.splitType.length === 0) {
    dimensions.push({
      name: 'Split Type',
      passed: true,
      detail: 'rule matches any',
    });
  } else if (!order.splitType) {
    dimensions.push({
      name: 'Split Type',
      passed: false,
      detail: `rule requires ${rule.splitType.map((v) => SPLIT_LABELS[v]).join(' or ')}, order has none`,
    });
  } else if (rule.splitType.includes(order.splitType)) {
    dimensions.push({
      name: 'Split Type',
      passed: true,
      detail: `order is ${SPLIT_LABELS[order.splitType]}`,
    });
  } else {
    dimensions.push({
      name: 'Split Type',
      passed: false,
      detail: `order is ${SPLIT_LABELS[order.splitType]}, rule requires ${rule.splitType.map((v) => SPLIT_LABELS[v]).join(' or ')}`,
    });
  }

  if (rule.splitIndex.length > 0) {
    if (order.splitIndex == null) {
      dimensions.push({
        name: 'Quarter',
        passed: false,
        detail: `rule requires ${rule.splitIndex.map((i) => `Q${i + 1}`).join(' or ')}, order has none`,
      });
    } else if (rule.splitIndex.includes(order.splitIndex)) {
      dimensions.push({
        name: 'Quarter',
        passed: true,
        detail: `order is Q${order.splitIndex + 1}`,
      });
    } else {
      dimensions.push({
        name: 'Quarter',
        passed: false,
        detail: `order is Q${order.splitIndex + 1}, rule requires ${rule.splitIndex.map((i) => `Q${i + 1}`).join(' or ')}`,
      });
    }
  }

  if (rule.producer.length > 0) {
    if (!order.producerId) {
      dimensions.push({
        name: 'Producer',
        passed: false,
        detail: 'rule constrains producer, order has none',
      });
    } else if (rule.producer.includes(order.producerId)) {
      dimensions.push({
        name: 'Producer',
        passed: true,
        detail: 'order producer is in rule set',
      });
    } else {
      dimensions.push({
        name: 'Producer',
        passed: false,
        detail: 'order producer is not in rule set',
      });
    }
  }

  if (rule.buyer.length > 0) {
    if (!order.buyerId) {
      dimensions.push({
        name: 'Buyer',
        passed: false,
        detail: 'rule constrains buyer, order has none',
      });
    } else if (rule.buyer.includes(order.buyerId)) {
      dimensions.push({
        name: 'Buyer',
        passed: true,
        detail: 'order buyer is in rule set',
      });
    } else {
      dimensions.push({
        name: 'Buyer',
        passed: false,
        detail: 'order buyer is not in rule set',
      });
    }
  }

  return {
    ruleIndex: -1,
    dimensions,
    allPassed: dimensions.every((d) => d.passed),
  };
}

export interface RuleTesterDialogProps {
  open: boolean;
  rules: LocalRule[];
  questionnaires: { _id: string; name: string }[];
  defaultQuestionnaireId: string;
  vendorId?: string;
  onClose: () => void;
}

const STEPS = ['Configure Test Order', 'See Matching Result'];

export const RuleTesterDialog: FC<RuleTesterDialogProps> = ({
  open,
  rules,
  questionnaires,
  defaultQuestionnaireId,
  vendorId,
  onClose,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [order, setOrder] = useState<TestOrder>(EMPTY_ORDER);

  const [producerSelected, setProducerSelected] =
    useState<SearchableCustomer | null>(null);
  const [buyerSelected, setBuyerSelected] = useState<SearchableCustomer | null>(
    null,
  );

  const producerSearch = useCustomerSearch({
    vendorId,
    relationshipType: EnumUserType.Producer,
    enabled: open,
  });
  const buyerSearch = useCustomerSearch({
    vendorId,
    relationshipType: EnumUserType.Buyer,
    producerId: order.producerId,
    enabled: open,
  });

  const handleEntered = () => {
    setActiveStep(0);
    setOrder(EMPTY_ORDER);
    setProducerSelected(null);
    setBuyerSelected(null);
    producerSearch.reset();
    buyerSearch.reset();
  };

  const showSplitIndex =
    !!order.splitType && SPLIT_INDEX_ELIGIBLE.has(order.splitType);

  const questionnaireName = (id: string) =>
    questionnaires.find((q) => q._id === id)?.name ?? id;

  const evaluations = useMemo(
    () =>
      rules.map((rule, i) => ({
        ...evaluateRule(order, rule),
        ruleIndex: i,
      })),
    [rules, order],
  );

  const winningIndex = evaluations.findIndex((e) => e.allPassed);
  const winningRule = winningIndex >= 0 ? rules[winningIndex] : null;

  const quarterLabels =
    order.splitType === EnumCutsheetAssignmentRuleSetRulesSplitType.Quarters
      ? ['Quarter 1', 'Quarter 2', 'Quarter 3', 'Quarter 4']
      : ['Quarter 1', 'Quarter 2'];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEntered: handleEntered }}
    >
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Simulate a hypothetical order to see which questionnaire the
              current rule set would assign.
            </Typography>

            <Stack direction="row" spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Inspection Level</InputLabel>
                <Select
                  label="Inspection Level"
                  value={order.inspectionLevel ?? ''}
                  onChange={(e) =>
                    setOrder((prev) => ({
                      ...prev,
                      inspectionLevel:
                        (e.target
                          .value as EnumCutsheetAssignmentRuleSetRulesInspectionLevel) ||
                        undefined,
                    }))
                  }
                >
                  <MenuItem value="">— None —</MenuItem>
                  {Object.values(
                    EnumCutsheetAssignmentRuleSetRulesInspectionLevel,
                  ).map((v) => (
                    <MenuItem key={v} value={v}>
                      {INSPECTION_LABELS[v]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Split Type</InputLabel>
                <Select
                  label="Split Type"
                  value={order.splitType ?? ''}
                  onChange={(e) =>
                    setOrder((prev) => {
                      const next = {
                        ...prev,
                        splitType:
                          (e.target
                            .value as EnumCutsheetAssignmentRuleSetRulesSplitType) ||
                          undefined,
                      };
                      if (
                        !next.splitType ||
                        !SPLIT_INDEX_ELIGIBLE.has(next.splitType)
                      ) {
                        next.splitIndex = undefined;
                      }
                      return next;
                    })
                  }
                >
                  <MenuItem value="">— None —</MenuItem>
                  {Object.values(
                    EnumCutsheetAssignmentRuleSetRulesSplitType,
                  ).map((v) => (
                    <MenuItem key={v} value={v}>
                      {SPLIT_LABELS[v]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showSplitIndex && (
                <FormControl fullWidth size="small">
                  <InputLabel>Quarter</InputLabel>
                  <Select
                    label="Quarter"
                    value={order.splitIndex ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value as number | '';
                      setOrder((prev) => ({
                        ...prev,
                        splitIndex: raw === '' ? undefined : Number(raw),
                      }));
                    }}
                  >
                    <MenuItem value="">— None —</MenuItem>
                    {quarterLabels.map((label, i) => (
                      <MenuItem key={i} value={i}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            <Stack spacing={2} direction="row">
              <Autocomplete
                fullWidth
                options={producerSearch.options}
                loading={producerSearch.loading}
                filterOptions={(x) => x}
                getOptionLabel={(opt) => opt.fullName}
                value={producerSelected}
                inputValue={producerSearch.input}
                onInputChange={(_e, val, reason) => {
                  if (reason === 'reset') return;
                  producerSearch.onInputChange(val);
                }}
                onChange={(_e, val) => {
                  setProducerSelected(val);
                  setBuyerSelected(null);
                  buyerSearch.reset();
                  setOrder((prev) => ({
                    ...prev,
                    producerId: val?._id,
                    buyerId: undefined,
                  }));
                  producerSearch.onInputChange(val?.fullName ?? '');
                }}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producer (optional)"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {producerSearch.loading && (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete
                fullWidth
                options={buyerSearch.options}
                loading={buyerSearch.loading}
                filterOptions={(x) => x}
                getOptionLabel={(opt) => opt.fullName}
                value={buyerSelected}
                inputValue={buyerSearch.input}
                onInputChange={(_e, val, reason) => {
                  if (reason === 'reset') return;
                  buyerSearch.onInputChange(val);
                }}
                onChange={(_e, val) => {
                  setBuyerSelected(val);
                  setOrder((prev) => ({ ...prev, buyerId: val?._id }));
                  buyerSearch.onInputChange(val?.fullName ?? '');
                }}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      producerSelected
                        ? `Buyer for ${producerSelected.fullName} (optional)`
                        : 'Buyer (optional)'
                    }
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {buyerSearch.loading && (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          </Stack>
        )}

        {activeStep === 1 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {rules.length === 0 ? (
              <Alert severity="info">
                No rules configured.{' '}
                {defaultQuestionnaireId
                  ? `This order would use the default form: ${questionnaireName(defaultQuestionnaireId)}.`
                  : 'This order would require manual form selection.'}
              </Alert>
            ) : (
              <>
                <Alert
                  severity={
                    winningRule
                      ? 'success'
                      : defaultQuestionnaireId
                        ? 'info'
                        : 'warning'
                  }
                >
                  {winningRule ? (
                    <>
                      <strong>Rule #{winningIndex + 1}</strong> matches. This
                      order would be assigned:{' '}
                      <strong>
                        {questionnaireName(winningRule.questionnaireId)}
                      </strong>
                      .
                    </>
                  ) : defaultQuestionnaireId ? (
                    <>
                      No rule matches. This order would use the default form:{' '}
                      <strong>
                        {questionnaireName(defaultQuestionnaireId)}
                      </strong>
                      .
                    </>
                  ) : (
                    <>
                      No rule matches and no default form is configured — this
                      order would require manual form selection.
                    </>
                  )}
                </Alert>

                <Stack spacing={1}>
                  {evaluations.map((evaluation, i) => {
                    const rule = rules[i];
                    const isWinner = i === winningIndex;
                    const isSkippedAfterMatch =
                      winningIndex >= 0 && i > winningIndex;

                    const status = isSkippedAfterMatch
                      ? 'skipped'
                      : isWinner
                        ? 'matched'
                        : 'failed';

                    return (
                      <Card
                        key={rule.uid}
                        variant="outlined"
                        sx={{
                          borderColor: isWinner
                            ? 'success.main'
                            : isSkippedAfterMatch
                              ? 'divider'
                              : undefined,
                          borderWidth: isWinner ? 2 : 1,
                          opacity: isSkippedAfterMatch ? 0.55 : 1,
                        }}
                      >
                        <CardContent
                          sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                          >
                            {status === 'matched' && (
                              <CheckCircleIcon
                                color="success"
                                fontSize="small"
                              />
                            )}
                            {status === 'failed' && (
                              <CancelIcon color="error" fontSize="small" />
                            )}
                            {status === 'skipped' && (
                              <PendingIcon
                                fontSize="small"
                                sx={{ color: 'text.disabled' }}
                              />
                            )}
                            <Typography variant="body2" fontWeight={600}>
                              #{i + 1}
                              {rule.label ? ` — ${rule.label}` : ''}
                            </Typography>
                            <Box flex={1} />
                            <Chip
                              size="small"
                              label={questionnaireName(rule.questionnaireId)}
                              color="primary"
                              variant="outlined"
                            />
                            {isWinner && (
                              <Chip
                                size="small"
                                label="MATCH"
                                color="success"
                              />
                            )}
                            {isSkippedAfterMatch && (
                              <Chip
                                size="small"
                                label="Not evaluated"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          {!isSkippedAfterMatch && (
                            <Stack spacing={0.25} sx={{ pl: 3 }}>
                              {evaluation.dimensions.map((dim, di) => (
                                <Box
                                  key={di}
                                  display="flex"
                                  alignItems="center"
                                  gap={0.75}
                                >
                                  {dim.passed ? (
                                    <CheckCircleIcon
                                      sx={{
                                        fontSize: 14,
                                        color: 'success.main',
                                      }}
                                    />
                                  ) : (
                                    <CancelIcon
                                      sx={{
                                        fontSize: 14,
                                        color: 'error.main',
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    <strong>{dim.name}:</strong> {dim.detail}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {activeStep === 1 && (
          <Button onClick={() => setActiveStep(0)}>Back</Button>
        )}
        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={() => setActiveStep(1)}
            disabled={!order.inspectionLevel && !order.splitType}
          >
            Evaluate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
